// Lazily-fetching combobox for config-form fields annotated with
// `gundi:reference` in a runner's ui_schema. See:
//   docs/superpowers/specs/2026-08-03-reference-select-widget-design.md
//   gundi-integration-cmore:docs/rfc-reference-data-portal-support.md
//
// Invariants this widget must never violate:
//   1. The form never blocks on reference data — every failure mode degrades
//      to a plain text input, with a non-intrusive warning.
//   2. Saved config is never auto-mutated — a stored value missing from
//      fetched options gets a warning badge, never cleared or replaced.
//   3. Empty fields fetch lazily (menu-open only); a field that already holds
//      a saved value fetches on mount so the stored (often opaque) ID renders
//      as its display label. Both paths rely on the in-flight/TTL cache in
//      src/api/referenceOptions.ts to keep the fan-out cheap.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Ajv from 'ajv';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { WidgetProps } from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from '../HoverTooltip';
import { useEnterOpensMenu } from '../useEnterOpensMenu';
import { AttentionIcon } from '../ProjectIcons';
import { defaultStyle } from '../componentstyles/selectinputstyles';
import {
  dataRefPath,
  idToDataPath,
  resolveDataRef,
  resolveDataRefTitle,
} from './referencePath';
import {
  fetchReferenceOptions,
  fetchDraftReferenceOptions,
  DraftReferenceState,
  ReferenceDataResponse,
  ReferenceOption,
  hashString,
  stableStringify,
} from '../../../api/referenceOptions';
import {
  fetchProviderReferenceOptions,
  fetchDestinationReferenceOptions,
} from '../../../api/providerTargets';
import { isEphemeralExecutionEnabled } from '../ephemeralExecution';

// `optional: true` on a $data param: when the referenced value is empty the
// param is left out of the request instead of holding the fetch back.
export type ReferenceAnnotationParam =
  unknown | { $data: string; optional?: boolean };

export type ReferenceSearchSpec = { param: string; min_chars?: number };

export type ReferenceAnnotation = {
  action: string;
  target?: 'self' | 'provider' | 'destination';
  params?: Record<string, ReferenceAnnotationParam>;
  allow_free_text?: boolean;
  /** Typeahead (spec §1–2 of the search extension): when present, the widget
   * fetches on debounced input instead of menu open, sending the typed text
   * as params[search.param]. */
  search?: ReferenceSearchSpec;
};

export type FlatSelectOption = {
  value: string;
  label: string;
  description?: string | null;
};
export type GroupedSelectOption = {
  label: string;
  options: FlatSelectOption[];
};
export type SelectOptions = FlatSelectOption[] | GroupedSelectOption[];

export type ResolvedAnnotationParams = {
  /** Fully resolved params (literals passed through, $data refs resolved against rootFormData). */
  params: Record<string, unknown>;
  /** Keys of params whose $data ref resolved to empty/undefined/null (or couldn't be resolved at all). */
  missing: string[];
  /** Keys of optional $data params left out of the request because they were empty. */
  omitted: string[];
};

function isDataRefSpec(
  value: unknown,
): value is { $data: string; optional?: boolean } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as any).$data === 'string'
  );
}

// An array counts as empty for an optional param when it holds nothing but
// blanks, e.g. the row the user just added and hasn't filled in yet.
function isEmptyOptionalValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.every(isEmptyValue);
  return isEmptyValue(value);
}

function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

// One AJV instance for widget-side auth validation — same primitive the hook
// uses. strict:false because integration schemas carry non-standard keywords.
const authAjv = new Ajv({ strict: false, allErrors: true });

/** True when the widget should NOT fetch yet because auth isn't usable. If the
 *  type has no auth action (public source), returns false — no gate. If it has
 *  auth, prefers the schema-aware AJV check (matches the Test Connection
 *  button's isAuthComplete) so rjsf's default authentication_type won't count
 *  as "user typed" and so a field of type array/object left empty ([] / {})
 *  isn't treated as "provided". Falls back to the every-blank heuristic when
 *  no schema was carried in draftReference. */
export function isDraftAuthEmpty(
  draft: DraftReferenceState | undefined,
): boolean {
  if (!draft) return true;
  const authValue = draft.authValue ?? 'auth';
  const auth =
    draft.configurations.find((c) => c.actionType === 'auth') ??
    draft.configurations.find((c) => c.actionValue === authValue);
  // Type has no auth action registered AND no auth-shaped config exists
  // (buildDraftReference leaves authValue undefined for public sources) —
  // nothing to gate on, let the widget fetch.
  if (!auth && draft.authValue === undefined) return false;
  if (!auth) return true;
  const data = auth.data ?? {};
  if (draft.authSchema) {
    // Strip empties (including empty arrays/objects) before AJV: `required`
    // only checks key presence, so a blank field would pass without this.
    const nonEmpty: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v) && v.length === 0) continue;
      if (
        typeof v === 'object' &&
        !Array.isArray(v) &&
        Object.keys(v as object).length === 0
      )
        continue;
      nonEmpty[k] = v;
    }
    try {
      const validate = authAjv.compile(draft.authSchema);
      validate(nonEmpty);
      return (validate.errors ?? []).some((e) => e.keyword === 'required');
    } catch {
      // Unusual schema — fall through to heuristic below.
    }
  }
  const values = Object.values(data);
  return values.every((v) => v === undefined || v === null || v === '');
}

/** Pure decision logic for step 2 of the spec: resolve every `params` entry (literal
 * pass-through or `$data` relative reference), and report which ones came back empty
 * so the caller can gate the fetch. `id` is the field's own rjsf id (e.g.
 * "root_event_type_to_tag_0_field_mappings_1_cmore_field_name"); `rootSchema` /
 * `rootFormData` come from `registry.rootSchema` / `formContext.rootFormData`. */
export function resolveAnnotationParams(
  annotation: ReferenceAnnotation | undefined,
  id: string,
  rootSchema: any,
  rootFormData: any,
  // rjsf's id root for the form this field belongs to. Omitted means "root",
  // rjsf's own default.
  idPrefix?: string,
): ResolvedAnnotationParams {
  if (!annotation?.params) return { params: {}, missing: [], omitted: [] };

  const fieldPath = idToDataPath(id, rootSchema, idPrefix);
  const params: Record<string, unknown> = {};
  const missing: string[] = [];
  const omitted: string[] = [];

  for (const [key, raw] of Object.entries(annotation.params)) {
    if (isDataRefSpec(raw)) {
      const resolved = fieldPath
        ? resolveDataRef(raw.$data, fieldPath, rootFormData)
        : undefined;
      if (raw.optional === true) {
        // Blank rows (e.g. one just added) are dropped from the list so the
        // action never has to look up an empty value.
        if (!isEmptyOptionalValue(resolved)) {
          params[key] = Array.isArray(resolved)
            ? resolved.filter((v) => !isEmptyValue(v))
            : resolved;
        } else {
          omitted.push(key);
        }
        continue;
      }
      if (isEmptyValue(resolved)) missing.push(key);
      params[key] = resolved;
    } else {
      params[key] = raw;
    }
  }

  return { params, missing, omitted };
}

/** Pure mapping from fetched options to react-select's option shape: label falls back
 * to value, and options are grouped (react-select's `{label, options}[]` grouped form)
 * as soon as any option carries a `group` — ungrouped options among them land in a
 * trailing untitled group rather than being dropped. */
export function buildSelectOptions(options: ReferenceOption[]): SelectOptions {
  const flat: FlatSelectOption[] = options.map((o) => ({
    value: o.value,
    label: o.label ?? o.value,
    description: o.description,
  }));

  const hasGroup = options.some((o) => !!o.group);
  if (!hasGroup) return flat;

  const groups = new Map<string, FlatSelectOption[]>();
  const ungrouped: FlatSelectOption[] = [];
  options.forEach((o, i) => {
    if (o.group) {
      if (!groups.has(o.group)) groups.set(o.group, []);
      groups.get(o.group)!.push(flat[i]);
    } else {
      ungrouped.push(flat[i]);
    }
  });

  const grouped: GroupedSelectOption[] = Array.from(groups.entries()).map(
    ([label, opts]) => ({ label, options: opts }),
  );
  if (ungrouped.length) grouped.push({ label: '', options: ungrouped });
  return grouped;
}

export function isGroupedOptions(
  options: SelectOptions,
): options is GroupedSelectOption[] {
  return (
    options.length > 0 &&
    Array.isArray((options[0] as GroupedSelectOption).options)
  );
}

function flattenOptions(options: SelectOptions): FlatSelectOption[] {
  return isGroupedOptions(options)
    ? options.flatMap((g) => g.options)
    : options;
}

/** Step 6 of the spec: a saved value that isn't among the fetched options gets a
 * warning badge — but is never cleared. Only meaningful once a fetch has actually
 * succeeded (callers should not call this against options: [] before the first fetch). */
export function isStaleValue(
  value: unknown,
  options: ReferenceOption[],
): boolean {
  if (isEmptyValue(value)) return false;
  return !options.some((o) => o.value === value);
}

function toSelectedOption(
  value: unknown,
  flat: FlatSelectOption[],
): FlatSelectOption | null {
  if (isEmptyValue(value)) return null;
  const stringValue = String(value);
  return (
    flat.find((o) => o.value === stringValue) ?? {
      value: stringValue,
      label: stringValue,
    }
  );
}

type FetchState =
  | { status: 'not_fetched' }
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'success';
      options: ReferenceOption[];
      truncated?: boolean;
      targetCount?: number;
    };

/** How long a fetch may run before the loading message escalates from
 * "Loading options…" to reassurance that a slow (cold-start) fetch is normal. */
export const SLOW_LOADING_THRESHOLD_MS = 4000;

/** Typeahead: debounce between the last keystroke and the fetch. Implementation
 * detail (deliberately not part of the contract). */
export const SEARCH_DEBOUNCE_MS = 300;
/** Typeahead: contract default for search.min_chars. */
export const DEFAULT_SEARCH_MIN_CHARS = 2;
/** Debounce window for draft-state changes before the widget resets and
 * re-fetches. Typing in a text-input field (e.g. EarthRanger site name)
 * would otherwise fire one runner call per keystroke through cdip to the
 * source system — rate-limit / lockout risk. */
export const DRAFT_RESET_DEBOUNCE_MS = 400;

/** Menu content shown while options are being fetched (react-select
 * `LoadingMessage` override). A fetch can legitimately take several seconds —
 * the runner behind the execute proxy may be cold-starting — so after
 * SLOW_LOADING_THRESHOLD_MS the text escalates to say that explicitly instead
 * of leaving the operator staring at a silent menu. */
export const ReferenceLoadingMessage = () => {
  const { t } = useTranslation();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_LOADING_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-ui-text-primary-text"
      role="status"
    >
      {/* Thick-stroke arc spinner: at 16px the standard donut spinner's thin
                arc all but disappears, so this uses a bold rounded arc in full
                brand green over a faint track, spinning slightly faster than
                Tailwind's 1s default to read as clearly active. */}
      <svg
        aria-hidden="true"
        className="h-4 w-4 shrink-0 animate-spin text-ui-cta-primary"
        style={{ animationDuration: '0.7s' }}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth="4.5"
        />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          strokeWidth="4.5"
          strokeLinecap="round"
        />
      </svg>
      <span className="animate-pulse">
        {slow
          ? t('Still working — fetching live options can take a few seconds…')
          : t('Loading options…')}
      </span>
    </div>
  );
};

const ReferenceSelectWidget = (props: WidgetProps) => {
  const { id, value, disabled, readonly, onChange, registry, uiSchema } = props;
  const TextWidget = registry.widgets.TextWidget;
  const { t } = useTranslation();

  const annotation = uiSchema?.['gundi:reference'] as
    ReferenceAnnotation | undefined;
  const formContext = (registry.formContext ?? {}) as {
    rootFormData?: any;
    integrationId?: string;
    authToken?: string;
    idPrefix?: string;
    draftReference?: DraftReferenceState;
  };
  const { rootFormData, integrationId, authToken, idPrefix, draftReference } =
    formContext;

  // integrationId wins over draftReference so edit surfaces keep today's
  // behavior verbatim. A linked target ("provider" or "destination") in
  // draft mode collapses to text — nothing is linked yet in creation flows.
  const isProviderTarget = annotation?.target === 'provider';
  const isDestinationTarget = annotation?.target === 'destination';
  const isLinkedTarget = isProviderTarget || isDestinationTarget;
  const mode: 'integration' | 'draft' | 'fallback' = (() => {
    if (!annotation || !authToken) return 'fallback';
    if (integrationId) return 'integration';
    if (
      draftReference &&
      !isLinkedTarget &&
      isEphemeralExecutionEnabled(draftReference.typeId)
    )
      return 'draft';
    return 'fallback';
  })();
  const useDefaultWidget = mode === 'fallback';

  const { params, missing, omitted } = useMemo(
    () =>
      resolveAnnotationParams(
        annotation,
        id,
        registry.rootSchema,
        rootFormData,
        idPrefix,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [annotation, id, registry.rootSchema, rootFormData, idPrefix],
  );
  const paramsKey = JSON.stringify(params);
  // Optional params can point at a list the user is editing (e.g. the saved
  // projects of the very array this dropdown sits in), so they change on each
  // keystroke, add or remove. That portion of the reset key is debounced like
  // the draft; required params still reset at once.
  const optionalParamKeys = Object.entries(annotation?.params ?? {})
    .filter(([, raw]) => isDataRefSpec(raw) && raw.optional === true)
    .map(([key]) => key);
  const requiredParamsKey = JSON.stringify(
    Object.fromEntries(
      Object.entries(params).filter(
        ([key]) => !optionalParamKeys.includes(key),
      ),
    ),
  );
  const optionalParamsKey = JSON.stringify(
    Object.fromEntries(
      Object.entries(params).filter(([key]) => optionalParamKeys.includes(key)),
    ),
  );
  const [debouncedOptionalParamsKey, setDebouncedOptionalParamsKey] =
    useState(optionalParamsKey);
  useEffect(() => {
    if (debouncedOptionalParamsKey === optionalParamsKey) return;
    const timeoutId = setTimeout(
      () => setDebouncedOptionalParamsKey(optionalParamsKey),
      DRAFT_RESET_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [optionalParamsKey, debouncedOptionalParamsKey]);
  // Hash the stringified draft so a change of typed credentials still
  // invalidates the reset key, but the raw token/password never lives in
  // React state (the debounced copy below is heap-retained until the next
  // draft edit). Same FNV-1a hash the reference-options cache key uses.
  const draftKey =
    mode === 'draft' ? hashString(stableStringify(draftReference)) : '';
  // The draft portion is debounced (see the effect below) so a text-input
  // in the schema form doesn't fire a runner call per keystroke; the params
  // portion resets immediately because a cascade change (e.g. selecting a
  // tag from a parent dropdown) is a discrete user commit, not typing.
  const [debouncedDraftKey, setDebouncedDraftKey] = useState(draftKey);
  useEffect(() => {
    if (debouncedDraftKey === draftKey) return;
    const timeoutId = setTimeout(
      () => setDebouncedDraftKey(draftKey),
      DRAFT_RESET_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [draftKey, debouncedDraftKey]);
  const resetKey =
    requiredParamsKey +
    '|' +
    debouncedOptionalParamsKey +
    '|' +
    debouncedDraftKey;
  // The key a fetch fired right now would settle to once both debounces catch
  // up. A fetch records this rather than resetKey, so a fetch fired inside a
  // debounce window isn't thrown away when the window closes on the same data.
  const settledResetKey =
    requiredParamsKey + '|' + optionalParamsKey + '|' + draftKey;

  const searchSpec = annotation?.search;
  const isSearchMode = !!searchSpec?.param;
  const minChars = searchSpec?.min_chars ?? DEFAULT_SEARCH_MIN_CHARS;
  const [searchText, setSearchText] = useState('');

  // Same Enter-opens-menu convention as SelectInput — these selects are
  // reached by tabbing through a schema form, where Enter otherwise did
  // nothing. Only one of the render paths below mounts at a time, so the
  // single ref always points at the live select.
  //
  // openMenu() snapshots the focusable options before onMenuOpen triggers the
  // lazy fetch, so on a cold open the menu momentarily has none. No extra
  // focus handling is needed for that: react-select's getNextFocusedOption
  // falls back to options[0] whenever the options prop changes and the
  // previously focused option is absent, so focus lands as soon as the fetch
  // resolves (verified in-app).
  const { selectRef, handleKeyDown } = useEnterOpensMenu();

  const [fetchState, setFetchState] = useState<FetchState>({
    status: 'not_fetched',
  });
  const requestIdRef = useRef(0);
  const prevResetKeyRef = useRef(resetKey);
  // The resetKey the in-flight fetch was fired against. If it still matches
  // the current resetKey when the reset effect runs (the fetch was against
  // post-change data), the reset is a no-op — dropping it would leave an
  // open menu stuck with no options.
  const fetchResetKeyRef = useRef('');

  // Requirement 7: when the resolved params change (by JSON identity), drop any
  // fetched options — the next menu-open re-fetches (cheap: TTL cache) — and never
  // touch props.value. The stale-value check itself is derived below, so resetting
  // fetchState to "not_fetched" here automatically re-runs it (no stale badge shows
  // until the next successful fetch, per requirement 6).
  // Bumping requestIdRef here too is load-bearing: without it, a slow fetch that was
  // still in flight for the OLD params would pass the requestId guard in runFetch's
  // .then/.catch when it eventually resolves, overwriting this reset with a
  // "success" (or "error") for params that no longer apply — e.g. pinning a false
  // stale-value badge until the next menu open.
  // Same reset also fires for draft edits (token changes), so a stale-credential
  // response can never land after the user has fixed it.
  useEffect(() => {
    if (prevResetKeyRef.current === resetKey) {
      // No debounce pending, yet the result came from other data: an edit was
      // made and undone inside the window after a fetch fired on the edit, so
      // resetKey never moved. Drop that result the same way.
      if (
        resetKey === settledResetKey &&
        fetchState.status !== 'not_fetched' &&
        fetchResetKeyRef.current !== settledResetKey
      ) {
        requestIdRef.current++;
        setFetchState({ status: 'not_fetched' });
      }
      return;
    }
    prevResetKeyRef.current = resetKey;
    // Skip when a fetch fired AT the current resetKey is still in flight:
    // the debounce merely caught up to the same value the fetch already
    // used, so its result is valid. Dropping it here would leave an open
    // dropdown showing nothing until the user closes and reopens it.
    // Same once it has succeeded or failed: an open menu would otherwise lose
    // the options it is showing and, with no saved value, never refetch.
    if (
      fetchState.status !== 'not_fetched' &&
      fetchResetKeyRef.current === resetKey
    )
      return;
    requestIdRef.current++;
    setFetchState({ status: 'not_fetched' });
  }, [resetKey, settledResetKey, fetchState.status]);

  // Gate the widget on the DEBOUNCED draft-auth-empty state — the raw
  // draftAuthEmpty flips false the moment the user types their first
  // character, and a downstream effect keyed on it would fire an undebounced
  // fetch against a single-character credential. The button already uses
  // its own AJV gate so this only matters for the widget's prefetch path.
  const rawDraftAuthEmpty =
    mode === 'draft' && isDraftAuthEmpty(draftReference);
  const [draftAuthEmpty, setDraftAuthEmpty] = useState(rawDraftAuthEmpty);
  useEffect(() => {
    if (draftAuthEmpty === rawDraftAuthEmpty) return;
    const timeoutId = setTimeout(
      () => setDraftAuthEmpty(rawDraftAuthEmpty),
      DRAFT_RESET_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [rawDraftAuthEmpty, draftAuthEmpty]);

  // Typeahead (spec §2): input-driven fetching. Below min_chars nothing is
  // fetched and previously fetched options are dropped, so the menu shows the
  // type-to-search hint rather than results for an older query. An unresolved
  // $data dependency (or, in draft mode, empty credentials) blocks fetching
  // regardless of typed text. Keyed on resetKey rather than paramsKey so the
  // search re-runs whenever the reset effect above drops results — including
  // a debounced draft-credential edit — instead of leaving the menu empty
  // until the next keystroke.
  // The requestId bump runs on every query change, before the debounce is
  // scheduled — not only when the next fetch starts. Otherwise a fetch still
  // in flight for an older query ("le") that resolves inside the debounce
  // window for the new one ("tiger") passes the guard in runFetch and renders
  // its results as matches for the current text.
  // Results that already landed for the older query are cleared the same way
  // (fetchState → loading) rather than kept until the debounced fetch starts:
  // with client filtering off they would stay selectable as matches for the
  // new text. The selected value lives in props.value, so it is unaffected.
  useEffect(() => {
    if (!isSearchMode || useDefaultWidget) return;
    requestIdRef.current++;
    const query = searchText.trim();
    if (query.length < minChars) {
      setFetchState({ status: 'not_fetched' });
      return;
    }
    if (missing.length > 0 || draftAuthEmpty) return;
    setFetchState({ status: 'loading' });
    const timer = setTimeout(() => {
      runFetch({ ...params, [searchSpec!.param]: query });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSearchMode,
    useDefaultWidget,
    searchText,
    minChars,
    missing.length,
    draftAuthEmpty,
    resetKey,
  ]);

  // Params of the most recent fetch, so Retry replays exactly what failed —
  // in search mode that includes the typed query, which the default (resolved
  // base params only) would drop.
  const lastFetchParamsRef = useRef<Record<string, unknown>>(params);

  const runFetch = (fetchParams: Record<string, unknown> = params) => {
    const requestId = ++requestIdRef.current;
    lastFetchParamsRef.current = fetchParams;
    fetchResetKeyRef.current = settledResetKey;
    setFetchState({ status: 'loading' });
    // target: "provider" resolves the Routes' provider integrations and
    // target: "destination" the connection's destinations, fanning the
    // reference action out to each one (union+dedupe); target: "self"
    // (default) queries the integration being configured. Same fetch
    // lifecycle either way.
    const fetchPromise = (() => {
      if (mode === 'draft') {
        return fetchDraftReferenceOptions(
          authToken as string,
          annotation!.action,
          fetchParams,
          draftReference as DraftReferenceState,
        );
      }
      if (isProviderTarget) {
        return fetchProviderReferenceOptions(
          authToken as string,
          integrationId as string,
          annotation!.action,
          fetchParams,
        );
      }
      if (isDestinationTarget) {
        return fetchDestinationReferenceOptions(
          authToken as string,
          integrationId as string,
          annotation!.action,
          fetchParams,
        );
      }
      return fetchReferenceOptions(
        authToken as string,
        integrationId as string,
        annotation!.action,
        fetchParams,
      );
    })();
    fetchPromise
      .then((resp: ReferenceDataResponse & { targetCount?: number }) => {
        if (requestIdRef.current !== requestId) return; // superseded by a newer fetch
        setFetchState({
          status: 'success',
          options: resp.options ?? [],
          truncated: resp.truncated,
          // Only the linked-target responses carry targetCount;
          // both response shapes are assignable to this widened type,
          // so no cast is needed.
          targetCount: resp.targetCount,
        });
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setFetchState({ status: 'error' });
      });
  };

  // Requirement 3a (amends "lazy" in requirement 3): a field that already
  // carries a saved value fetches as soon as it can — on mount, and again
  // whenever the params-change effect above resets fetchState — so the stored
  // (often opaque) ID renders as its display label without the operator having
  // to open the menu. Empty fields keep the original menu-open-only laziness.
  // The mount-time fan-out is bounded: only fields with values and fully
  // resolved cascade params fetch, and the in-flight/TTL cache in
  // referenceOptions.ts collapses sibling rows sharing (action, params) into
  // one request. The not_fetched guard makes re-runs a no-op once a fetch is
  // loading/succeeded/failed; paramsKey is in the deps so a cascade change
  // (which resets fetchState above) re-triggers the prefetch.
  useEffect(() => {
    if (
      useDefaultWidget ||
      isSearchMode ||
      isEmptyValue(value) ||
      missing.length > 0
    )
      return;
    if (draftAuthEmpty) return; // draft mode with no creds — nothing to prefetch against
    if (fetchState.status !== 'not_fetched') return;
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useDefaultWidget,
    value,
    missing.length,
    fetchState.status,
    paramsKey,
    draftAuthEmpty,
  ]);

  if (useDefaultWidget) {
    return <TextWidget {...props} />;
  }

  const handleMenuOpen = () => {
    if (isSearchMode) return; // typeahead fetches on input, never on open
    if (missing.length > 0) return; // never fetch with unresolved cascade params
    if (draftAuthEmpty) return;
    // Once per (params, draft) window, not once per open. The resetKey effect
    // above re-arms it on a cascade/draft change; "error" is covered on
    // purpose — retrying belongs to the text widget's Retry button.
    if (fetchState.status !== 'not_fetched') return;
    runFetch();
  };

  const commonSelectProps = {
    ref: selectRef,
    onKeyDown: handleKeyDown,
    inputId: id,
    isDisabled: disabled || readonly,
    isClearable: true,
    // Portal the open menu to <body>: inside the rjsf form each array item
    // establishes its own stacking context, so an in-place menu paints
    // underneath the next item's move/delete controls regardless of its
    // z-index. Portaling escapes those contexts (and any overflow clipping).
    menuPortalTarget:
      typeof document !== 'undefined' ? document.body : undefined,
    styles: {
      ...defaultStyle,
      menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
    },
    // Animated, escalating feedback while options load (fetches can take
    // several seconds on a cold-started runner).
    components: { LoadingMessage: ReferenceLoadingMessage },
    onMenuOpen: handleMenuOpen,
    onChange: (option: FlatSelectOption | null) =>
      onChange(option?.value ?? undefined),
    onInputChange: (input: string, meta: { action: string }) => {
      if (isSearchMode && meta.action === 'input-change') setSearchText(input);
    },
    // When a list is showing, the typed value is one row among the matches;
    // "Use" says what picking it does (react-select's default says "Create").
    formatCreateLabel: (input: string) =>
      t('Use "{{value}}"', { value: input }),
  };

  // A combobox with nothing to pick from yet (no credentials, an unresolved
  // cascade param, no provider offering suggestions): what the user types IS the
  // value, so it is kept as they type and no menu opens to repeat it back.
  const freeTextOnly = (
    hint: string,
    placeholderStyle?: (provided: any) => any,
  ) => (
    <CreatableSelect
      {...commonSelectProps}
      {...(placeholderStyle
        ? {
            styles: {
              ...commonSelectProps.styles,
              placeholder: placeholderStyle,
            },
          }
        : {})}
      options={[]}
      value={value ? { value, label: String(value) } : null}
      // The saved value lives in the text box itself, not as a selected
      // chip, so clicking in lets the user edit it where it stands instead
      // of react-select starting an empty search that replaces it. Blur and
      // menu-close try to reset the text; the value owns it, so they can't.
      inputValue={value == null ? '' : String(value)}
      controlShouldRenderValue={false}
      // react-select focuses its input itself when the box is clicked, which
      // leaves the caret at the start; put it after the text so typing
      // continues the value.
      onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
        const el = e.target;
        requestAnimationFrame(() =>
          el.setSelectionRange?.(el.value.length, el.value.length),
        );
      }}
      placeholder={hint}
      menuIsOpen={false}
      onInputChange={(input: string, meta: { action: string }) => {
        if (meta.action === 'input-change') onChange(input || undefined);
      }}
    />
  );

  if (draftAuthEmpty) {
    return freeTextOnly(t('Enter credentials first'));
  }

  // Requirement 2: any $data param still unresolved → no fetch; free-text combobox
  // (no options to pick from yet) with a hint naming what to fill in first.
  // Typing a value still works while other fields are blank, so the hint says
  // so rather than demanding them first; it names each field by its title.
  const fillInHint = (keys: string[]) => {
    const fieldPath = idToDataPath(id, registry.rootSchema, idPrefix);
    const fields = keys
      .map((key) => {
        const raw = annotation?.params?.[key];
        const ref = isDataRefSpec(raw) ? raw.$data : undefined;
        return (
          (ref &&
            fieldPath &&
            resolveDataRefTitle(ref, fieldPath, registry.rootSchema)) ||
          (ref ? ref.split('/').pop() : undefined) ||
          key
        );
      })
      .join(', ');
    return t('Type a value, or fill in {{fields}} to pick from a list', {
      fields,
    });
  };

  if (missing.length > 0) {
    const hint = fillInHint(missing);
    // The hint names a field by its title, which a connector may make long, so
    // it stays on one line and is cut with "…". The whole sentence lives only in
    // the native hover tooltip.
    return (
      <div title={hint}>
        {freeTextOnly(hint, (provided: any) => ({
          ...provided,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }))}
      </div>
    );
  }

  // Requirement 5: a failed fetch degrades to the plain-input experience (never a
  // dead-end select) plus a warning + retry affordance local to this field.
  if (fetchState.status === 'error') {
    return (
      <div>
        <TextWidget {...props} />
        <div className="mt-1 flex items-center gap-1 text-sm text-ui-text-primary-text">
          <HoverTooltip
            content={t("Couldn't load options — enter a value manually")}
          >
            <span>
              <AttentionIcon />
            </span>
          </HoverTooltip>
          <button
            type="button"
            className="text-ui-cta-primary hover:underline"
            onClick={() => runFetch(lastFetchParamsRef.current)}
          >
            {t('Retry')}
          </button>
        </div>
      </div>
    );
  }

  // Provider-resolution spec: "if none has it, the field stays free text" — a
  // successful fetch that resolved zero matching targets is not an error (no
  // warning badge, no retry affordance), just a plain free-text combobox naming why.
  if (
    isLinkedTarget &&
    fetchState.status === 'success' &&
    fetchState.targetCount === 0
  ) {
    return freeTextOnly(
      isProviderTarget
        ? t('No connected provider offers suggestions')
        : t('No connected destination offers suggestions'),
    );
  }

  const isLoading = fetchState.status === 'loading';
  const fetchedOptions =
    fetchState.status === 'success' ? fetchState.options : [];
  const selectOptions = buildSelectOptions(fetchedOptions);
  const flatOptions = flattenOptions(selectOptions);
  const selectedOption = toSelectedOption(value, flatOptions);
  // Requirement 6: only meaningful once a fetch has actually succeeded — before that,
  // fetchedOptions is [] and we must not flag an as-yet-unverified saved value as stale.
  const stale =
    !isSearchMode &&
    fetchState.status === 'success' &&
    isStaleValue(value, fetchedOptions);

  // An empty list while optional fields were left blank: filling them in may be
  // what brings options back, so the menu says which ones instead of "No options".
  // A param pointing at the list this field sits in (Project IDs reading its own
  // projects) isn't named: the user is already filling that one in.
  const hintKeys = omitted.filter((key) => {
    const raw = annotation?.params?.[key];
    const fieldPath = idToDataPath(id, registry.rootSchema, idPrefix);
    const target =
      isDataRefSpec(raw) && fieldPath
        ? dataRefPath(raw.$data, fieldPath)
        : null;
    const containsField =
      !!target &&
      fieldPath!.length > target.length &&
      target.every((seg, i) => String(seg) === String(fieldPath![i]));
    return !containsField;
  });
  const emptyHint =
    fetchState.status === 'success' &&
    fetchedOptions.length === 0 &&
    hintKeys.length > 0
      ? fillInHint(hintKeys)
      : undefined;

  const allowFreeText = annotation!.allow_free_text !== false;
  const SelectComponent: any = allowFreeText ? CreatableSelect : Select;
  const extraProps = allowFreeText
    ? { onCreateOption: (input: string) => onChange(input) }
    : {};

  const searchHint = t('Type at least {{count}} characters to search', {
    count: minChars,
  });
  // Server-side filtering: react-select must not re-filter (a match may not
  // contain the typed substring, e.g. a taxon matched via a synonym).
  const searchProps = isSearchMode
    ? {
        filterOption: null,
        placeholder: searchHint,
        noOptionsMessage: () =>
          searchText.trim().length < minChars
            ? searchHint
            : t('No matching options'),
        // isValidNewOption is deliberately left at react-select's default:
        // min_chars gates the search request only, not manual entry, so a
        // valid one-character free-text value (e.g. "1") stays creatable.
      }
    : {};

  return (
    <div className="flex items-center gap-1">
      <div className="min-w-0 flex-1">
        <SelectComponent
          {...commonSelectProps}
          {...extraProps}
          {...searchProps}
          options={selectOptions}
          value={selectedOption}
          isLoading={isLoading}
          {...(emptyHint ? { noOptionsMessage: () => emptyHint } : {})}
        />
      </div>
      {stale && (
        <HoverTooltip content={t('Saved value not in the current list')}>
          <span>
            <AttentionIcon />
          </span>
        </HoverTooltip>
      )}
    </div>
  );
};

export default ReferenceSelectWidget;
